/**
 * The pages under Preferred Network in the left navigation, by the type of the signed-in user.
 * Each entry names the route it opens and the message id of its link text. The navigation item and
 * its flyout read this list and nothing else, so a page is added to the navigation here.
 *
 * A candidate has one page of their own, My Network, which the navigation opens directly, with no
 * flyout.
 */

import { teamRoleOf } from '../../util/teamRole';

export const NETWORK_BASE_PATH = '/preferred-network';
export const MY_NETWORK_PATH = '/my-network';

export const NETWORK_PAGES = Object.freeze({
  customer: Object.freeze([
    Object.freeze({ key: 'talent', routeName: 'PreferredTalentPage', labelId: 'PreferredNetwork.nav.preferredTalent' }),
    Object.freeze({ key: 'suppliers', routeName: 'PreferredSuppliersPage', labelId: 'PreferredNetwork.nav.preferredSuppliers' }),
  ]),
  agency: Object.freeze([
    Object.freeze({ key: 'talent', routeName: 'PreferredTalentPage', labelId: 'PreferredNetwork.nav.consultants' }),
    Object.freeze({ key: 'suppliers', routeName: 'PreferredSuppliersPage', labelId: 'PreferredNetwork.nav.suppliers' }),
    Object.freeze({ key: 'customers', routeName: 'NetworkCustomersPage', labelId: 'PreferredNetwork.nav.customers' }),
  ]),
  candidate: Object.freeze([]),
});

const MY_NETWORK_PAGE = Object.freeze({ key: 'myNetwork', routeName: 'MyNetworkPage', labelId: 'PreferredNetwork.nav.myNetwork' });

/** The Preferred Network pages for this type of user; none for a type without any. */
export const networkPagesFor = userType =>
  Object.prototype.hasOwnProperty.call(NETWORK_PAGES, userType) ? [...NETWORK_PAGES[userType]] : [];

/**
 * What the navigation shows this type of user: a flyout of pages, one page opened directly, or
 * nothing.
 */
export const networkNavFor = userType => {
  if (userType === 'candidate') return { kind: 'direct', page: { ...MY_NETWORK_PAGE } };
  const pages = networkPagesFor(userType);
  return pages.length ? { kind: 'flyout', pages } : null;
};

/** Which side of the network the talent page shows this type of user, or null. */
export const talentSideFor = userType =>
  userType === 'customer' ? 'talent' : userType === 'agency' ? 'consultants' : null;

/** Which side of the network the suppliers page shows this type of user: a customer's suppliers, or null. */
export const suppliersSideFor = userType => (userType === 'customer' ? 'suppliers' : null);

/** Which side of the network holds a supplier's own sub-suppliers, or null for anyone else. */
export const subSuppliersSideFor = userType => (userType === 'agency' ? 'subSuppliers' : null);

/**
 * Which side the Suppliers page reads for this type of user: a customer's preferred suppliers, a
 * supplier's own sub-suppliers, or null.
 */
export const supplierListSideFor = userType => suppliersSideFor(userType) || subSuppliersSideFor(userType);

/**
 * Which side of the network the invitee's own page shows this type of user: a candidate's companies
 * and suppliers, a supplier's customers, or null.
 */
export const inviteeSideFor = userType => (userType === 'candidate' ? 'mine' : userType === 'agency' ? 'customers' : null);

/**
 * The sections of a supplier's Customers page: the customers that listed it, and the suppliers that
 * source from it.
 */
export const CUSTOMERS_SECTIONS = Object.freeze(['customers', 'primes']);

/** The section of a page the address asks for: one of `sections`, else the first; null without sections. */
export const sectionFromSearch = (sections, search) => {
  if (!sections) return null;
  const asked = new URLSearchParams(search || '').get('section');
  return sections.includes(asked) ? asked : sections[0];
};

// The team roles that manage a company's side of the network; the server checks the same roles.
const NETWORK_MANAGER_ROLES = ['Admin', 'HR/Talent Acquisition', 'Tech Acquisition'];

/** Whether the signed-in member manages their company's network, by the role the server recorded. */
export const managesNetwork = currentUser => NETWORK_MANAGER_ROLES.includes(teamRoleOf(currentUser));

/**
 * Whether the person may answer and leave on the invitee's own page: a candidate always, for
 * themself; a supplier's member only when they manage its network.
 */
export const answersForInviteeSide = (userType, currentUser) => userType !== 'agency' || managesNetwork(currentUser);

const isUnder = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);

/** Whether an address is one of the Preferred Network pages. */
export const isNetworkPath = pathname =>
  typeof pathname === 'string' && (isUnder(pathname, NETWORK_BASE_PATH) || isUnder(pathname, MY_NETWORK_PATH));
